import { useTranslation } from 'react-i18next';
import {
  ANNOUNCEMENT_FONTS as F,
  ANNOUNCEMENT_PALETTE as P,
  announcementButtonBase,
  type AnnouncementCardProps,
} from '../announcement-style';

const STYLES = `
@keyframes announcementExtraIn {
  from { opacity: 0; transform: translateY(24px); }
  to { opacity: 1; transform: translateY(0); }
}
@keyframes announcementTicker {
  to { transform: translateX(-100%); }
}
.announcement-extra { max-width: 600px; border-radius: 4px; }
.announcement-extra .announcement-extra-inner { padding: 24px 30px 28px; }
.announcement-extra .announcement-extra-handle { display: none; }
.announcement-extra .announcement-extra-ticker span { display: inline-block; padding-left: 100%; animation: announcementTicker 18s linear infinite; }
.announcement-extra h2 { font-size: clamp(1.9rem, 6.5vw, 2.9rem); }
.announcement-extra .announcement-extra-band::after { content: ''; flex: 1; height: 1px; background: rgba(242,160,196,.35); }
.announcement-extra .announcement-extra-press:hover:not(:disabled) { transform: translateY(-2px); }
.announcement-extra .announcement-extra-press:active:not(:disabled) { transform: translateY(4px); box-shadow: 0 2px 0 ${P.redShadow}; }
@media (prefers-reduced-motion: reduce) {
  .announcement-extra .announcement-extra-ticker span { animation: none; padding-left: 1rem; }
}
@container announcement (max-width: 700px) {
  .announcement-extra { max-width: none; border-radius: 18px 18px 0 0; }
  .announcement-extra .announcement-extra-inner { padding: 20px 20px calc(30px + env(safe-area-inset-bottom, 0px)); }
  .announcement-extra .announcement-extra-handle { display: block; }
}
`;

/** Splits the headline so its last word can be tinted charm pink. */
function splitHeadline(title: string): { head: string; last: string } {
  const words = title.trim().split(/\s+/);
  const last = words.pop() ?? '';
  return { head: words.join(' '), last };
}

/**
 * "Edição extra" — ink-black sheet like the magazine's agenda band and back
 * cover: red running ticker on top, charm-pink band label, big condensed
 * headline with the last word in pink, serif body, pressable red CTA with a
 * hard shadow. Centered on desktop; rises from the bottom edge on phones.
 */
export function ExtraEditionCard(props: AnnouncementCardProps) {
  const { t } = useTranslation('p2e');
  const { head, last } = splitHeadline(props.title);
  const ticker = `${t('announcements.extraTicker')} · `;

  return (
    <article
      role="dialog"
      aria-modal="true"
      aria-label={props.title}
      className="announcement-extra"
      onClick={(event) => event.stopPropagation()}
      style={{
        position: 'relative',
        width: '100%',
        maxHeight: '90vh',
        overflowY: 'auto',
        background: P.ink,
        color: P.paper,
        boxShadow: '0 30px 70px rgba(0,0,0,.6)',
        animation: props.reducedMotion ? undefined : 'announcementExtraIn .35s ease both',
      }}
    >
      <style>{STYLES}</style>

      <div
        aria-hidden="true"
        className="announcement-extra-ticker"
        style={{
          background: P.maxRed,
          color: P.paper,
          fontFamily: F.cond,
          fontSize: 10.5,
          fontWeight: 600,
          letterSpacing: '.3em',
          textTransform: 'uppercase',
          padding: '8px 0',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
        }}
      >
        <span>{ticker.repeat(3)}</span>
      </div>

      <div className="announcement-extra-inner">
        <div
          aria-hidden="true"
          className="announcement-extra-handle"
          style={{ width: 42, height: 4, borderRadius: 2, background: 'rgba(255,248,245,.3)', margin: '0 auto 14px' }}
        />

        <p
          className="announcement-extra-band"
          style={{
            fontFamily: F.cond,
            fontSize: 11,
            fontWeight: 600,
            letterSpacing: '.3em',
            textTransform: 'uppercase',
            color: P.charm,
            margin: '0 0 16px',
            display: 'flex',
            alignItems: 'center',
            gap: 16,
          }}
        >
          {t('announcements.masthead')}
        </p>

        <h2
          style={{
            fontFamily: F.cond,
            textTransform: 'uppercase',
            fontWeight: 700,
            lineHeight: 0.95,
            margin: '0 0 14px',
            textWrap: 'balance',
          }}
        >
          {head ? `${head} ` : null}
          {last && <span style={{ color: P.charm }}>{last}</span>}
        </h2>

        {props.body && (
          <p
            style={{
              fontFamily: F.serif,
              fontSize: 16,
              lineHeight: 1.6,
              color: P.mutedOnDark,
              margin: '0 0 24px',
              maxWidth: '48ch',
              whiteSpace: 'pre-line',
            }}
          >
            {props.body}
          </p>
        )}

        {props.media && <div style={{ marginBottom: 22 }}>{props.media}</div>}

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center' }}>
          <button
            ref={props.primaryRef}
            type="button"
            disabled={props.primaryDisabled}
            onClick={props.onPrimary}
            className="announcement-extra-press"
            style={{
              ...announcementButtonBase,
              background: P.maxRed,
              color: P.white,
              boxShadow: `0 6px 0 ${P.redShadow}`,
              minHeight: 46,
              padding: '0 28px',
              borderRadius: 3,
              transition: props.reducedMotion ? undefined : 'transform .12s ease, box-shadow .12s ease',
              opacity: props.primaryDisabled ? 0.45 : 1,
              cursor: props.primaryDisabled ? 'default' : 'pointer',
            }}
          >
            {props.primaryLabel}
          </button>
          {props.skippable && (
            <button
              type="button"
              onClick={props.onSkip}
              style={{
                ...announcementButtonBase,
                background: 'transparent',
                color: P.charm,
                borderColor: 'rgba(242,160,196,.45)',
              }}
            >
              {props.skipLabel}
            </button>
          )}
        </div>

        {props.gatedHint && (
          <p style={{ fontFamily: F.serif, fontStyle: 'italic', fontSize: 13, color: P.mutedOnDark, margin: '12px 0 0' }}>
            {props.gatedHint}
          </p>
        )}
      </div>
    </article>
  );
}
